import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CONSTRAINT_BUCKETS } from '../data/wiseCheckQuestions';
import jsPDF from 'jspdf';
import './WiseCheckResults.css';
import { gaTrackEvent } from "../utils/ga";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const WiseCheckResults = ({ scores, answers, flags }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [bookingStatus, setBookingStatus] = useState('');
  
  useEffect(() => {
    gaTrackEvent("wise_check_completed");
  }, []);

  // Calculate results
  const sortedScores = Object.entries(scores)
    .map(([bucket, score]) => ({ bucket, score }))
    .sort((a, b) => b.score - a.score);

  const primaryConstraint = sortedScores[0];
  const secondaryConstraint = sortedScores[1].score > 35 ? sortedScores[1] : null;

  // Determine intervention type
  const getInterventionType = (score) => {
    if (score >= 85) return 'Stabilisation';
    if (score >= 71) return 'Restructure';
    if (score >= 56) return 'Systemisation';
    if (score >= 40) return 'Optimisation';
    return 'Maintenance';
  };

  const interventionType = getInterventionType(primaryConstraint.score);

  // Get score interpretation
  const getScoreInterpretation = (score) => {
    if (score >= 81) return 'Systemic risk';
    if (score >= 61) return 'Critical constraint';
    if (score >= 41) return 'Active constraint';
    if (score >= 21) return 'Emerging friction';
    return 'Healthy';
  };

  // Generate PDF
  const generatePDF = () => {
    console.log('PDF generation started');
    try {
      const doc = new jsPDF();
      console.log('jsPDF instance created');
      
      // Set default font
      doc.setFont('helvetica');
      
      // Title
      doc.setFontSize(22);
      doc.setTextColor(26, 35, 50);
      doc.text('Wise Check', 20, 20);
      doc.text('Diagnostic Summary', 20, 30);
      
      // Subtitle
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Generated: ' + new Date().toLocaleDateString(), 20, 40);
      
      // Draw line
      doc.setDrawColor(92, 225, 230);
      doc.setLineWidth(0.5);
      doc.line(20, 45, 190, 45);
      
      // Primary Constraint
      doc.setFontSize(14);
      doc.setTextColor(92, 225, 230);
      doc.text('Primary Constraint', 20, 55);
      
      doc.setFontSize(16);
      doc.setTextColor(26, 35, 50);
      const primaryText = String(primaryConstraint.bucket);
      doc.text(primaryText, 20, 65);
      
      doc.setFontSize(12);
      doc.text('Score: ' + primaryConstraint.score, 20, 73);
      doc.text('Level: ' + getScoreInterpretation(primaryConstraint.score), 20, 80);
      
      // Secondary Constraint
      let yPos = 95;
      if (secondaryConstraint) {
        doc.setFontSize(14);
        doc.setTextColor(203, 220, 106);
        doc.text('Secondary Constraint', 20, yPos);
        
        doc.setFontSize(12);
        doc.setTextColor(26, 35, 50);
        doc.text(String(secondaryConstraint.bucket), 20, yPos + 8);
        doc.text('Score: ' + secondaryConstraint.score, 20, yPos + 15);
        yPos += 30;
      } else {
        yPos = 90;
      }
      
      // Intervention Type
      doc.setFontSize(14);
      doc.setTextColor(92, 225, 230);
      doc.text('Recommended Intervention', 20, yPos);
      
      doc.setFontSize(14);
      doc.setTextColor(26, 35, 50);
      doc.setFont('helvetica', 'bold');
      doc.text(String(interventionType), 20, yPos + 10);
      doc.setFont('helvetica', 'normal');
      
      // Next Steps box
      yPos += 25;
      doc.setDrawColor(92, 225, 230);
      doc.setLineWidth(1);
      doc.rect(20, yPos, 170, 60);
      
      doc.setFontSize(12);
      doc.setTextColor(26, 35, 50);
      doc.setFont('helvetica', 'bold');
      doc.text('Next Steps', 25, yPos + 8);
      doc.setFont('helvetica', 'normal');
      
      doc.setFontSize(10);
      doc.text('This is your Wise Check summary. If you want a clear next step,', 25, yPos + 18);
      doc.text('book a session with Mediawise and we’ll turn the findings into a', 25, yPos + 23);
      doc.text('simple priority plan that fits your business.', 25, yPos + 28);
      doc.text('We will cover:', 25, yPos + 38);
      doc.text('  - Your full diagnostic breakdown', 25, yPos + 43);
      doc.text('  - What to fix first (and what can wait)', 25, yPos + 48);
      doc.text('  - A practical 90-day plan (if you want one)', 25, yPos + 53);
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('© 2025 Mediawise Solutions', 20, 280);
      doc.text('Contact: mitch@mediawisesolutions.com.au | Phone: 0409 265 711', 20, 285);
      
      // Save the PDF
      const filename = 'wise-check-summary-' + Date.now() + '.pdf';
      doc.save(filename);
      console.log('PDF saved successfully:', filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF: ' + error.message + '. Please try again or contact support.');
    }
  };

  // Handle booking submission
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingStatus('');

    try {
      await axios.post(`${API}/booking`, bookingForm);
      setBookingStatus('success');
      setBookingForm({ name: '', email: '', phone: '', message: 'Keen to review my Wise Check results and confirm the best next step.' });
      setTimeout(() => {
        setShowBookingModal(false);
        setBookingStatus('');
      }, 2000);
    } catch (error) {
      setBookingStatus('error');
      console.error('Booking error:', error);
    }
  };

  // Save results to backend - prevent duplicate calls across re-renders
  const submissionRef = useRef({ submitted: false });

  useEffect(() => {
    const saveResults = async () => {
      // Strict duplicate prevention
      if (submissionRef.current.submitted || isSaving || saved) {
        console.log('Skipping duplicate submission');
        return;
      }

      // Mark as submitted immediately
      submissionRef.current.submitted = true;
      setIsSaving(true);

      try {
        const response = await axios.post(`${API}/wise-check-results`, {
          scores,
          answers,
          flags,
          primary_constraint: primaryConstraint.bucket,
          primary_score: primaryConstraint.score,
          secondary_constraint: secondaryConstraint?.bucket,
          secondary_score: secondaryConstraint?.score,
          intervention_type: interventionType,
          contact_info: answers.CONTACT
        });
        setSaved(true);
        console.log('Email sent:', response.data.email_sent);
      } catch (error) {
        console.error('Error saving results:', error);
        // Allow retry if the request fails
        submissionRef.current.submitted = false;
      } finally {
        setIsSaving(false);
      }
    };

    saveResults();
    // Intentionally run once on mount. If you need to re-save on prop changes,
    // convert this to a dependency-based effect with server-side idempotency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="results-container" data-testid="results-container">
      <div className="results-header">
        <h1 className="results-title" data-testid="results-title">Your Wise Check Summary</h1>
        <p className="results-subtitle">Here’s a snapshot of what’s most likely getting in the way right now.</p>
      </div>

      {/* Teaser Results */}
      <div className="teaser-results">
        {/* Primary Constraint - Limited Info */}
        <div className="constraint-card primary" data-testid="primary-constraint-card">
          <div className="constraint-badge">Primary friction area</div>
          <h2 className="constraint-name" data-testid="primary-constraint-name">
            {primaryConstraint.bucket}
          </h2>
          <div className="constraint-score-display">
            <span className="score-number" data-testid="primary-score">{primaryConstraint.score}</span>
            <span className="score-label">{getScoreInterpretation(primaryConstraint.score)}</span>
          </div>
          <p className="constraint-description teaser-text" data-testid="primary-description">
            This is the area most likely to be leaking effort right now. Your full breakdown (including contributing factors
            and priority actions) is ready — the next step is making sense of it in your context.
          </p>
        </div>

        {/* Intervention Type - Limited */}
        <div className="intervention-card teaser-card" data-testid="intervention-card">
          <h3>Recommended Approach</h3>
          <div className="intervention-type" data-testid="intervention-type">
            {interventionType}
          </div>
          <p className="intervention-description">
            Based on your responses, a <strong>{interventionType.toLowerCase()}</strong> approach is most likely to work best.
            If you’d like, we can walk through what that means and what to tackle first.
          </p>
        </div>

        {/* Locked Content Notice */}
        <div className="locked-content-notice" data-testid="locked-notice">
          <div className="lock-icon">🔒</div>
          <h3>Your full breakdown is ready</h3>
          <p>Your complete Wise Check results include:</p>
          <ul className="locked-features">
            <li>✓ Constraint breakdown across key areas of the business</li>
            <li>✓ Secondary constraints and contributing factors</li>
            <li>✓ Any risk flags and what they mean in plain English</li>
            <li>✓ A simple priority order (what to fix first, what can wait)</li>
            <li>✓ Practical next steps you can actually implement</li>
          </ul>
          <p className="locked-notice-footer">
            <strong>Your full results have been saved.</strong> If you’d like a clear next step,
            book a session and we’ll turn the findings into a simple priority plan.
          </p>
        </div>

        {/* Download Summary PDF */}
        <div className="pdf-download-section" data-testid="pdf-section">
          <h3>Download Your Summary</h3>
          <p>Get a PDF copy of this summary for your records.</p>
          <button 
            onClick={generatePDF}
            className="pdf-download-button"
            data-testid="pdf-download-button"
          >
            📄 Download Summary PDF
          </button>
        </div>

        {/* CTA Section */}
        <div className="results-cta locked-cta" data-testid="results-cta">
          <h3>Want to review the full results properly?</h3>
          <p>
            Book a complimentary 45-minute session with Mitch. We’ll walk through your full Wise Check,
            identify what to fix first, and make sure any next move (including marketing) has somewhere solid to land.
          </p>
          <div className="cta-buttons">
            <button 
              onClick={() => setShowBookingModal(true)}
              className="cta-button-primary"
              data-testid="cta-book-session"
            >
              Book a Clarity Session
            </button>
            <a 
              href="mailto:mitch@mediawisesolutions.com.au?subject=Wise Check - Strategy Session Request"
              className="cta-button-secondary"
              data-testid="cta-email"
            >
              Email Mitch
            </a>
            <a 
              href="tel:0409265711"
              className="cta-button-secondary"
              data-testid="cta-phone"
            >
              Call 0409 265 711
            </a>
          </div>
          <p className="results-note">
            {saved ? '✓ Your results have been saved. If you’d like, book a session and we’ll review them properly.' : 'Saving your results...'}
          </p>
        </div>

        {/* Back to Home */}
        <div className="back-home">
          <a href="/" className="back-home-link" data-testid="back-home">← Back to Home</a>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="modal-overlay" onClick={() => setShowBookingModal(false)} data-testid="booking-modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
            <button 
              onClick={() => setShowBookingModal(false)} 
              className="modal-close"
              data-testid="booking-modal-close"
            >
              ×
            </button>
            <h2 className="text-3xl font-bold text-navy mb-2" data-testid="booking-modal-title">
              Book a Clarity Session
            </h2>
            <p className="text-gray-600 mb-6">
              We’ll review your results, get clear on what matters most, and map out a practical next step.
            </p>
            
            {bookingStatus === 'success' && (
              <div className="alert-success" data-testid="booking-success-message">
                ✓ Thank you! We'll be in touch soon to schedule your session.
              </div>
            )}
            
            {bookingStatus === 'error' && (
              <div className="alert-error" data-testid="booking-error-message">
                ✗ Something went wrong. Please try again or contact us directly.
              </div>
            )}
            
            <form onSubmit={handleBookingSubmit} className="space-y-4" data-testid="booking-form">
              <div>
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  required
                  className="form-input"
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                  data-testid="booking-input-name"
                />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  required
                  className="form-input"
                  value={bookingForm.email}
                  onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                  data-testid="booking-input-email"
                />
              </div>
              <div>
                <label className="form-label">Phone *</label>
                <input
                  type="tel"
                  required
                  className="form-input"
                  value={bookingForm.phone}
                  onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                  data-testid="booking-input-phone"
                />
              </div>
              <div>
                <label className="form-label">Preferred Time</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={bookingForm.message}
                  onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                  placeholder="Let us know your preferred days/times for a strategy session..."
                  data-testid="booking-input-message"
                ></textarea>
              </div>
              <button 
                type="submit" 
                className="cta-button-large w-full"
                data-testid="booking-submit-button"
              >
                Request Clarity Session
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WiseCheckResults;
